import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Play, Pause, RotateCcw } from 'lucide-react';

const HLSPlayer = ({ playlistContent, keyPairId, policy, signature }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!playlistContent) {
      setError('No playlist content provided');
      return;
    }

    // Create a Blob URL from the playlist content
    const blob = new Blob([playlistContent], { type: 'application/vnd.apple.mpegurl' });
    const blobUrl = URL.createObjectURL(blob);
    blobUrlRef.current = blobUrl;

    if (Hls.isSupported()) {
      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        xhrSetup: (xhr, url) => {
          // Include CloudFront cookies
          // const cookies = document.cookie; // Get cookies from the document
          // xhr.withCredentials = true; // Allow credentials
          // xhr.setRequestHeader('Cookie', cookies); // Set the cookies in the request header

          // Check if the request is for a segment (not the playlist)
          if (url === blobUrl) {
            // This is the playlist URL, do not modify it
            xhr.open('GET', url, true);
          } else {
            // This is a segment URL, append CloudFront authentication parameters
            // const keyPairId = 'KA1I9KD5Q5I3B'; // Your Key-Pair-Id
            // const policy = 'eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly92aWRlby5kZXYubWVkZm9yYWxsLmNvbS9tZmEtaHViLVJmNlNORy8qIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzM0OTg0NzE4fX19XX0_'; // Your CloudFront policy
            // const signature = 'I8mIYuRRz5LqHma75gE7Cusc52RTLE7l2UGj3cHTlQRd1faURURVF1c8khQqx8v6DzBTr5cIlpSYClrTbKUc8BpTmHuQFpCdnY8fmYf64Pslt-2SOyABO-oAOe3x4Iez~KhWcq8xCGGE3zE3Zwrh4kU2KZ-MxsIhs577a1JO71HfDzKUnYIim0TtzatBSvoqfqwAK957tuJBHXgztQkIUuhmzl7MGLJxahQ8OWZpqCXlELscGzKxWX889zW0HXZNOo6WMLrtfVya-ff6IXIA9b4grMNQixyhJ2h1yjm3893v3I5CzyGK7f6Itv0jdEUpcGly3SyaJhnBMc26hdYQJA__'; // Your CloudFront signature

            const newUrl = `${url}?Key-Pair-Id=${keyPairId}&Policy=${encodeURIComponent(policy)}&Signature=${encodeURIComponent(signature)}`;
            xhr.open('GET', newUrl, true);
          }
        },
      });

      hlsRef.current = hls;

      // Bind video element and load source
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(blobUrl);
      });

      // Error handling
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setError('Network error occurred. Trying to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              setError('Media error occurred. Trying to recover...');
              hls.recoverMediaError();
              break;
            default:
              setError('An unrecoverable error occurred.');
              hls.destroy();
              break;
          }
        }
      });

      return () => {
        if (hls) {
          hls.destroy();
        }
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
      };
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Fallback for Safari
      videoRef.current.src = blobUrl;
      
      return () => {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
      };
    }
  }, [playlistContent]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleReload = () => {
    if (hlsRef.current) {
      hlsRef.current.stopLoad();
      hlsRef.current.startLoad();
      setError(null);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      <div className="relative bg-black rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          className="w-full h-full"
          playsInline
          controls={false}
        />
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handlePlayPause}
              className="p-2 rounded-full bg-white/90 hover:bg-white text-black"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6" />
              )}
            </button>
            <button
              onClick={handleReload}
              className="p-2 rounded-full bg-white/90 hover:bg-white text-black"
            >
              <RotateCcw className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HLSPlayer;